'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import useSessionStore from '@/stores/useSessionStore'
import { BookingFlowStep } from '@/types/bookingFlow'

import { routes } from '../routes'
import useStepsStore from '../stores/useStepsStore'

/**
 * Interface representing the return type of the useStep hook.
 * @template StepDataType - The type of the data associated with each step.
 */
export interface UseStepReturn<StepDataType extends Record<string, unknown>>
  extends BookingFlowStep<StepDataType> {
  data: StepDataType
  name: string
  next: () => void
  goto: (number: number) => void
  resetAt: (number?: number) => void
  steps: BookingFlowStep[]
  update: (data: StepDataType, completed?: boolean) => void
}

/**
 * Custom hook to manage the state and navigation of booking steps.
 * @template StepDataType - The type of the data associated with each step.
 * @returns {UseStepReturn<StepDataType>} - An object containing the current step data,
 * update function, next function, and steps array.
 */
export const useStep = <
  StepDataType extends Record<string, unknown>,
>(): UseStepReturn<StepDataType> => {
  const pathName = usePathname()
  const router = useRouter()
  const { steps, getStep, updateStep, setStepCompleted } = useStepsStore()
  const authToken = useSessionStore((state) => state.authToken)

  // Determine the current route index based on the current path
  const currentRouteIndex = useMemo(
    () => routes.findIndex(({ path }) => path === pathName),
    [pathName],
  )

  // Get the name of the current route
  const name = routes[currentRouteIndex]?.text || ''

  // Get the step number of the current route
  const number = routes[currentRouteIndex]?.stepNumber || 0

  // Determine the next route index and the corresponding route
  const nextRouteIndex = currentRouteIndex + 1
  let nextRoute = routes[nextRouteIndex]

  // If the next route is the login page and the user is already authenticated,
  // skip it by advancing one more route.
  if (nextRoute && nextRoute.path.includes('login') && authToken) {
    nextRoute = routes[nextRouteIndex + 1]
  }

  // Get the data and completion status of the current step
  const { data, completed } = useMemo(
    () => (number ? getStep(number) || {} : {}) as BookingFlowStep<StepDataType>,
    [getStep, number],
  )

  /**
   * Update the data and completion status of the current step.
   * @param {StepDataType} data - The data to update for the current step.
   * @param {boolean} [completed=false] - Whether the step is completed.
   */
  const update = useCallback(
    (data: StepDataType, completed = false) => {
      updateStep(number, data)
      setStepCompleted(number, completed)
    },
    [updateStep, number, setStepCompleted],
  )

  /**
   * Reset the completion status of steps starting from a given step number.
   * @param {number} [number=0] - The step number to start resetting from.
   * @returns {void}
   */
  const resetAt = useCallback(
    (number = 0) => {
      if (number < 1 || number > steps.length) return

      for (let i = number; i <= steps.length; i++) {
        setStepCompleted(i, false)
      }
    },
    [setStepCompleted, steps.length],
  )

  /**
   * Navigate to the next route in the booking flow.
   */
  const next = useCallback(() => {
    if (nextRoute) {
      const path = nextRoute.path
      const params = typeof window === 'undefined' ? '' : window.location.search.replace(/^\?/, '')

      return router.push(params ? `${path}?${params}` : path)
    }
  }, [nextRoute, router])

  const goto = useCallback(
    (index: number) => {
      const route = routes?.[index]

      if (route) {
        const path = route.path
        const params =
          typeof window === 'undefined' ? '' : window.location.search.replace(/^\?/, '')

        return router.push(params ? `${path}?${params}` : path)
      }
    },
    [router],
  )

  return {
    completed,
    data,
    name,
    next,
    number,
    resetAt,
    steps,
    update,
    goto,
  }
}
